import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { Fragment, useState } from "react";

import { changeCDRTotalKg, changePurchaseMode } from "../../store/cdrSlice";
import { useAppDispatch } from "../../store/hooks";
import type { Emission } from "../../types/Emissions";
import type { PurchaseType } from "../../types/cdr-request.type";

const classNames = (...classes: string[]) => {
  return classes.filter(Boolean).join(" ");
};

interface Props {
  list: Emission[];
}

const Select = ({ list }: Props) => {
  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState<Emission>(list[205]);
  const handleOnClick = (amount: number, purchaseMode: PurchaseType) => {
    dispatch(changeCDRTotalKg(amount));
    dispatch(changePurchaseMode(purchaseMode));
  };

  return (
    <Listbox value={selected} onChange={setSelected}>
      {({ open }) => (
        <>
          <div className="relative mt-2 w-60">
            <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pr-10 pl-3 text-left text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-2 focus:ring-indigo-600 focus:outline-none sm:text-sm sm:leading-6">
              <span className="block truncate">{selected.country}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="ring-opacity-5 absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black focus:outline-none sm:text-sm">
                {list.map((item: Emission) => (
                  <Listbox.Option
                    key={item.country}
                    className={({ active }) =>
                      classNames(
                        active ? "bg-indigo-600 text-white" : "text-gray-900",
                        "relative cursor-default py-2 pr-9 pl-3 select-none",
                      )
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? "font-semibold" : "font-normal",
                            "block truncate",
                          )}
                        >
                          {item.country}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? "text-white" : "text-indigo-600",
                              "absolute inset-y-0 right-0 flex items-center pr-4",
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
          <div>
            <p className="text-center">
              Average yearly CO₂ emissions per person in {selected.country}
            </p>
            <div className="my-2 flex justify-center">
              <button
                type="button"
                className="bg-primary hover:text-primary hover:border-primary focus:ring-primary mx-1 rounded-full border border-transparent px-7 py-2 font-header text-base font-bold text-white shadow-sm transition-all hover:bg-white focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
                onClick={() =>
                  handleOnClick(+selected.emissions_kg.toFixed(), "ONE_TIME")
                }
              >
                <p className="m-0 font-header font-bold">
                  {new Intl.NumberFormat("en-US").format(
                    +selected.emissions_kg.toFixed(),
                  )}{" "}
                  kg
                </p>
                <p className="m-0 text-sm italic">(remove in one)</p>
              </button>
              <button
                type="button"
                className="bg-primary hover:text-primary hover:border-primary focus:ring-primary mx-1 rounded-full border border-transparent px-7 py-2 font-header text-base font-bold text-white shadow-sm transition-all hover:bg-white focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
                onClick={() =>
                  handleOnClick(
                    +(selected.emissions_kg / 12).toFixed(),
                    "SUBSCRIPTION_MONTHLY",
                  )
                }
              >
                <p className="m-0 font-header font-bold">
                  {new Intl.NumberFormat("en-US").format(
                    +(selected.emissions_kg / 12).toFixed(),
                  )}{" "}
                  kg
                </p>
                <p className="m-0 text-sm italic">(remove monthly)</p>
              </button>
            </div>
          </div>
        </>
      )}
    </Listbox>
  );
};

export default Select;
